import { useCallback, useEffect, useState } from "react";

const SLOW_SPEED_MBPS = 2;
const MIN_TEST_FILE_BYTES = 512 * 1024;

const useInternetStatus = () => {
  const [status, setStatus] = useState("online");
  const [speed, setSpeed] = useState(null);

  const checkInternetSpeed = useCallback(async () => {
    if (!navigator.onLine) {
      setStatus("offline");
      setSpeed(null);
      return;
    }

    try {
      const response = await fetch(
        `/speed-test.bin?cacheBust=${Date.now()}`,
        {
          cache: "no-store",
        }
      );

      if (!response.ok) {
        throw new Error("Speed test failed");
      }

      const startTime = performance.now();
      const data = await response.blob();
      const endTime = performance.now();

      if (data.size < MIN_TEST_FILE_BYTES) {
        throw new Error("Speed test file is missing or incomplete");
      }

      const durationInSeconds = (endTime - startTime) / 1000;
      const speedMbps = (data.size * 8) / durationInSeconds / (1024 * 1024);

      if (!Number.isFinite(speedMbps)) {
        throw new Error("Speed test returned an invalid measurement");
      }

      setSpeed(speedMbps);

      if (speedMbps < SLOW_SPEED_MBPS) {
        setStatus("slow");
      } else {
        setStatus("online");
      }
    } catch (error) {
      console.error("Internet speed check failed:", error);
      setSpeed(null);

      if (!navigator.onLine) {
        setStatus("offline");
      } else {
        setStatus("slow");
      }
    }
  }, []);

  // Browser online/offline events
  useEffect(() => {
    const handleOffline = () => {
      setSpeed(null);
      setStatus("offline");
    };

    const handleOnline = () => {
      checkInternetSpeed();
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, [checkInternetSpeed]);

  // Initial check + periodic check
  useEffect(() => {
    checkInternetSpeed();

    const interval = setInterval(() => {
      checkInternetSpeed();
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [checkInternetSpeed]);

  return {
    status,
    speed,
  };
};

export default useInternetStatus;